// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Avatar,
  Button,
  Caption1,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Option,
} from '@fluentui/react-components';
import {
  ChevronDownRegular,
  ColorRegular,
  DismissRegular,
  LocalLanguageRegular,
  OptionsRegular,
  SignOutRegular,
} from '@fluentui/react-icons';
import { useIntl } from 'react-intl';
import type { UserPreferences } from '../hooks/useUserPreferences';
import type { ThemePreference } from '../hooks/useResolvedTheme';
import { getLocaleDisplayName, getLocaleCountryCode, fetchInstalledLocales, resolveInitialLocale } from '../i18n/locales';

interface UserProfileMenuProps {
  accountName: string;
  accountEmail: string;
  preferences: UserPreferences;
  onUpdatePreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;
  onSignOut: () => void;
}

export function UserProfileMenu({
  accountName,
  accountEmail,
  preferences,
  onUpdatePreference,
  onSignOut,
}: UserProfileMenuProps) {
  const intl = useIntl();
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [availableLocales, setAvailableLocales] = useState<string[]>(['en-GB']);

  // Load installed locales when preferences dialog opens (app-scoped)
  useEffect(() => {
    if (prefsOpen) {
      fetchInstalledLocales().then(setAvailableLocales).catch(() => {});
    }
  }, [prefsOpen]);

  const handleOpenPreferences = useCallback(() => {
    setPrefsOpen(true);
  }, []);

  const THEME_OPTIONS: ThemePreference[] = ['system', 'light', 'dark'];
  const themeLabels = useMemo<Record<ThemePreference, string>>(() => ({
    system: intl.formatMessage({ id: 'preferences.theme.system' }),
    light: intl.formatMessage({ id: 'preferences.theme.light' }),
    dark: intl.formatMessage({ id: 'preferences.theme.dark' }),
  }), [intl]);

  return (
    <>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <button type="button" className="user-profile-trigger" aria-label={accountName}>
            <Avatar name={accountName} size={28} />
            <Caption1 className="user-profile-name">{accountEmail}</Caption1>
            <ChevronDownRegular className="user-profile-chevron" />
          </button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<OptionsRegular />} onClick={handleOpenPreferences}>
              {intl.formatMessage({ id: 'userMenu.preferences' })}
            </MenuItem>
            <MenuItem icon={<SignOutRegular />} onClick={onSignOut}>
              {intl.formatMessage({ id: 'userMenu.signOut' })}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>

      <Dialog open={prefsOpen} onOpenChange={(_, data) => { setPrefsOpen(data.open); }}>
        <DialogSurface className="preferences-dialog">
          <DialogBody>
            <DialogTitle>
              {intl.formatMessage({ id: 'preferences.title' })}
            </DialogTitle>
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              aria-label="Close"
              className="preferences-close"
              onClick={() => { setPrefsOpen(false); }}
            />
            <DialogContent className="preferences-content">
              <div className="preferences-field">
                <label className="preferences-label">
                  <ColorRegular className="icon-inline" />
                  {intl.formatMessage({ id: 'preferences.theme' })}
                </label>
                <Dropdown
                  value={themeLabels[preferences.theme]}
                  selectedOptions={[preferences.theme]}
                  onOptionSelect={(_, data) => { onUpdatePreference('theme', data.optionValue as ThemePreference); }}
                  className="preferences-dropdown"
                >
                  {THEME_OPTIONS.map(opt => (
                    <Option key={opt} value={opt}>{themeLabels[opt]}</Option>
                  ))}
                </Dropdown>
              </div>

              <div className="preferences-field">
                <label className="preferences-label">
                  <LocalLanguageRegular className="icon-inline" />
                  {intl.formatMessage({ id: 'preferences.language' })}
                </label>
                <div className="locale-dropdown-wrapper">
                  {(() => {
                    const resolved = preferences.language || resolveInitialLocale();
                    const cc = getLocaleCountryCode(resolved);
                    return cc ? <span className={`fi fi-${cc} locale-dropdown-flag`} /> : null;
                  })()}
                  <Dropdown
                    value={(() => {
                      if (preferences.language) return getLocaleDisplayName(preferences.language, intl.locale);
                      const resolved = resolveInitialLocale();
                      const systemSupported = typeof navigator !== 'undefined' && resolved === navigator.language;
                      return systemSupported
                        ? intl.formatMessage({ id: 'preferences.language.system' })
                        : getLocaleDisplayName(resolved, intl.locale);
                    })()}
                    selectedOptions={[(() => {
                      if (preferences.language) return preferences.language;
                      const resolved = resolveInitialLocale();
                      const systemSupported = typeof navigator !== 'undefined' && resolved === navigator.language;
                      return systemSupported ? '' : resolved;
                    })()]}
                    onOptionSelect={(_, data) => { onUpdatePreference('language', data.optionValue ?? ''); }}
                    className="preferences-dropdown locale-dropdown-with-flag"
                  >
                  <Option value="">{intl.formatMessage({ id: 'preferences.language.system' })}</Option>
                  {availableLocales.map(code => {
                    const cc = getLocaleCountryCode(code);
                    return (
                      <Option key={code} value={code} text={getLocaleDisplayName(code, intl.locale)}>
                        {cc && <span className={`fi fi-${cc} locale-flag`} />}
                        {getLocaleDisplayName(code, intl.locale)}
                      </Option>
                    );
                  })}
                </Dropdown>
                </div>
              </div>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
